import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";
import PostCard from "./PostCard";
import EmojiInsertButton from "./EmojiInsertButton";

/**
 * Quote-repost composer: required commentary, destination picker, no title.
 * When actAsOrgId is set: org profile + owned communities only; never personal.
 */
const SocialQuoteRepostDialog = ({
  open,
  onOpenChange,
  accessToken,
  rootPost,
  onCreated,
  actAsOrgId = null,
  orgDisplayName = null,
  ownedCommunities = [],
}) => {
  const isActAsOrg = Boolean(actAsOrgId);
  const defaultDest = isActAsOrg ? "profile" : "personal";
  const [content, setContent] = useState("");
  const [destination, setDestination] = useState(defaultDest);
  const [communities, setCommunities] = useState([]);
  const [saving, setSaving] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setContent("");
    setDestination(isActAsOrg ? "profile" : "personal");
  }, [open, rootPost?.social_post_id, rootPost?.post_id, isActAsOrg]);

  useEffect(() => {
    if (!open || !accessToken) return;
    let cancelled = false;
    (async () => {
      if (isActAsOrg) {
        const list = (ownedCommunities || []).map((c) => ({
          community_id: c.community_id,
          display_name: c.display_name,
        }));
        if (!cancelled) setCommunities(list);
        return;
      }
      try {
        const res = await axios.get(
          `${API_BASE_URL}/social/explore/communities`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { limit: 50 },
          }
        );
        const list = (res.data?.communities || []).filter((c) => {
          const active = c.membership?.status === "active";
          const policy = c.posting_policy === "community_posts";
          const notOwning = !c.access?.is_owning_org_member;
          return active && policy && notOwning;
        });
        if (!cancelled) setCommunities(list);
      } catch {
        if (!cancelled) setCommunities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accessToken, isActAsOrg, ownedCommunities]);

  const rootId = rootPost?.social_post_id ?? rootPost?.post_id;
  const canSubmit = Boolean(content.trim()) && !saving && rootId != null;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        kind: "quote",
        content: content.trim(),
      };
      if (isActAsOrg) {
        if (destination !== "profile") {
          body.community_id = Number(destination);
        }
      } else if (destination !== "personal") {
        body.community_id = Number(destination);
      }
      const url = isActAsOrg
        ? `${API_BASE_URL}/org/${encodeURIComponent(actAsOrgId)}/social/posts/${encodeURIComponent(String(rootId))}/repost`
        : `${API_BASE_URL}/social/posts/${encodeURIComponent(String(rootId))}/repost`;
      const res = await axios.post(url, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toastSuccess("Quoted");
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not quote post"
      );
    } finally {
      setSaving(false);
    }
  };

  const profileLabel = orgDisplayName
    ? `${orgDisplayName} profile timeline`
    : "Organization profile timeline";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-visible sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Quote</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-visible">
          <div className="space-y-2 overflow-visible">
            <Label htmlFor="quote-content">Your commentary</Label>
            <textarea
              id="quote-content"
              ref={contentRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add a comment…"
              rows={4}
              disabled={saving}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
            <div className="relative z-10 flex items-center overflow-visible">
              <EmojiInsertButton
                value={content}
                onChange={setContent}
                textareaRef={contentRef}
                disabled={saving}
                inline
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quote-dest">Post to</Label>
            <select
              id="quote-dest"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              disabled={saving}
            >
              {isActAsOrg ? (
                <option value="profile">{profileLabel}</option>
              ) : (
                <option value="personal">Personal timeline</option>
              )}
              {communities.map((c) => (
                <option key={c.community_id} value={String(c.community_id)}>
                  {c.display_name}
                </option>
              ))}
            </select>
          </div>

          {rootPost ? (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2 pointer-events-none">
              <PostCard
                post={rootPost}
                socialHome
                relativeTimestamps
                socialTombstone
                embed
              />
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit}>
            {saving ? "Posting…" : "Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SocialQuoteRepostDialog;
